import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { DesignerLayout } from "@/components/layout/DesignerLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusPipeline } from "@/components/StatusPipeline";
import { ArrowLeft, UploadSimple, Download, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ProjectMessages } from "@/components/ProjectMessages";

const STATUSES = ["submitted", "preproduction", "design", "review", "final", "delivered"];

export default function DesignerProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/projects/${id}/status`, { status, note: statusNote });
      setStatusNote("");
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${id}/deliverables`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadFile = async (deliverable) => {
    if (deliverable.kind === "link" && deliverable.url) {
      window.open(deliverable.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const resp = await api.get(`/deliverables/${deliverable.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: deliverable.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = deliverable.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed");
    }
  };

  if (!project) return <DesignerLayout><div className="text-[#a3a3a3]">Loading…</div></DesignerLayout>;

  return (
    <DesignerLayout>
      <Link to="/designer/projects" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white mb-6" data-testid="designer-back">
        <ArrowLeft size={16} /> My projects
      </Link>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{project.service_type}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-2">{project.title}</h1>
        <div className="text-[#a3a3a3] mt-3 max-w-2xl">{project.description}</div>
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <span><strong>Client:</strong> {project.client_name}</span>
          <span><strong>Turnaround:</strong> {project.turnaround}</span>
          {project.target_date && <span><strong>Target:</strong> {project.target_date}</span>}
        </div>
      </div>

      <h2 className="font-display text-2xl mb-4 uppercase">Pipeline</h2>
      <StatusPipeline status={project.status} />

      <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">Update status</div>
        <input
          type="text"
          placeholder="Optional note for this update…"
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
          data-testid="designer-status-note"
          className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:border-[#FF2D2D] outline-none mb-3"
        />
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => updateStatus(s)}
              data-testid={`designer-set-status-${s}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                project.status === s
                  ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                  : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
              }`}
            >
              {project.status === s && <Check size={10} weight="bold" className="inline mr-1" />}
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl uppercase">Deliverables</h2>
          <label className="btn-primary px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 cursor-pointer">
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Uploading…" : "Upload"}
            <input ref={fileInputRef} type="file" className="hidden" onChange={onUpload} disabled={uploading} data-testid="designer-upload" />
          </label>
        </div>
        {!project.deliverables || project.deliverables.length === 0 ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">No files yet.</div>
        ) : (
          <div className="border border-[#262626] divide-y divide-[#262626]">
            {project.deliverables.map((d) => (
              <div key={d.id} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.original_filename}</div>
                  <div className="text-xs text-[#a3a3a3]">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                </div>
                <button onClick={() => downloadFile(d)} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2">
                  <Download size={14} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4 uppercase">Messages</h2>
        <ProjectMessages projectId={id} />
      </div>
    </DesignerLayout>
  );
}
